import { useCallback, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useCalendarStore } from '../store/useCalendarStore';
import {
  CreateCategoryData,
  CreateEventData,
  EventsByDate,
  FormattedEvent,
  GroupedEvents,
  UpdateCategoryData,
  UpdateEventData
} from '../types/calendar.types';

export const useCalendar = () => {
  // Use the global calendar store
  const {
    events,
    categories,
    loading,
    error,
    activeCategory,
    currentEvent,
    loadingEvent,
    fetchCalendarData: storeFetchCalendarData,
    fetchEvent: storeFetchEvent,
    createEvent: storeCreateEvent,
    updateEvent: storeUpdateEvent,
    deleteEvent: storeDeleteEvent,
    createCategory: storeCreateCategory,
    updateCategory: storeUpdateCategory,
    deleteCategory: storeDeleteCategory,
    toggleCategoryFilter: storeToggleCategoryFilter,
    clearCategoryFilter: storeClearCategoryFilter
  } = useCalendarStore();

  // Fetch a specific event by ID with toast notifications
  const fetchEvent = useCallback(async (eventId: string) => {
    try {
      const event = await storeFetchEvent(eventId);
      if (!event) {
        toast.error('Failed to load event details');
        throw new Error('Failed to load event details');
      }
      return event;
    } catch (err) {
      console.error('Error fetching event:', err);
      toast.error('Failed to load event details');
      throw err;
    }
  }, [storeFetchEvent]);

  // Fetch calendar data with toast notifications
  const fetchCalendarData = useCallback(async (forceRefresh: boolean = false) => {
    try {
      await storeFetchCalendarData(forceRefresh);
      if (error) {
        toast.error('Error fetching calendar data');
      }
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      toast.error('Error fetching calendar data');
    }
  }, [storeFetchCalendarData, error]);

  // Create event with toast notifications
  const createEvent = useCallback(async (data: CreateEventData) => {
    try {
      const newEvent = await storeCreateEvent(data);
      if (newEvent) {
        toast.success('Event created successfully');
        return newEvent;
      } else {
        toast.error('Failed to create event');
        throw new Error('Failed to create event');
      }
    } catch (err) {
      console.error('Error creating event:', err);
      toast.error('Failed to create event');
      throw err;
    }
  }, [storeCreateEvent]);

  // Update event with toast notifications
  const updateEvent = useCallback(async (eventId: string, data: UpdateEventData) => {
    try {
      const updatedEvent = await storeUpdateEvent(eventId, data);
      if (updatedEvent) {
        toast.success('Event updated successfully');
        return updatedEvent;
      } else {
        toast.error('Failed to update event');
        throw new Error('Failed to update event');
      }
    } catch (err) {
      console.error('Error updating event:', err);
      toast.error('Failed to update event');
      throw err;
    }
  }, [storeUpdateEvent]);

  // Delete event with toast notifications
  const deleteEvent = useCallback(async (eventId: string) => {
    try {
      const success = await storeDeleteEvent(eventId);
      if (success) {
        toast.success('Event deleted successfully');
      } else {
        toast.error('Failed to delete event');
        throw new Error('Failed to delete event');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event');
      throw err;
    }
  }, [storeDeleteEvent]);

  // Create category with toast notifications
  const createCategory = useCallback(async (data: CreateCategoryData) => {
    try {
      const newCategory = await storeCreateCategory(data);
      if (newCategory) {
        toast.success('Category created successfully');
        return newCategory;
      } else {
        toast.error('Failed to create category');
        throw new Error('Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      toast.error('Failed to create category');
      throw err;
    }
  }, [storeCreateCategory]);

  // Update category with toast notifications
  const updateCategory = useCallback(async (categoryId: string, data: UpdateCategoryData) => {
    try {
      const updatedCategory = await storeUpdateCategory(categoryId, data);
      if (updatedCategory) {
        toast.success('Category updated successfully');
        return updatedCategory;
      } else {
        toast.error('Failed to update category');
        throw new Error('Failed to update category');
      }
    } catch (err) {
      console.error('Error updating category:', err);
      toast.error('Failed to update category');
      throw err;
    }
  }, [storeUpdateCategory]);

  // Delete category with toast notifications
  const deleteCategory = useCallback(async (categoryId: string) => {
    try {
      const success = await storeDeleteCategory(categoryId);
      if (success) {
        toast.success('Category deleted successfully');
      } else {
        toast.error('Failed to delete category');
        throw new Error('Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error('Failed to delete category');
      throw err;
    }
  }, [storeDeleteCategory]);

  // Group events by date
  const eventsByDate = useMemo<EventsByDate>(() => {
    const result: EventsByDate = {};

    // Filter events by active category if set
    const filteredEvents = activeCategory
      ? events.filter(event => event.categoryId === activeCategory)
      : events;

    filteredEvents.forEach(event => {
      const dateKey = new Date(event.startDate.toDate()).toDateString();
      if (!result[dateKey]) {
        result[dateKey] = [];
      }
      result[dateKey].push(event);
    });

    return result;
  }, [events, activeCategory]);

  // Group events by category
  const eventsByCategory = useMemo<GroupedEvents>(() => {
    const result: GroupedEvents = {};

    events.forEach(event => {
      if (!result[event.categoryId]) {
        result[event.categoryId] = [];
      }
      result[event.categoryId].push(event);
    });

    return result;
  }, [events]);

  // Format events for the calendar component
  const formattedEvents = useMemo<FormattedEvent[]>(() => {
    return events
      .filter(event => !activeCategory || event.categoryId === activeCategory)
      .map(event => {
        const category = categories.find(cat => cat.id === event.categoryId);
        return {
          id: event.id,
          eventStart: event.startDate.toDate(),
          eventEnd: event.endDate?.toDate(),
          eventName: event.title,
          eventColor: category?.color || 'gray',
          description: event.description,
          url: event.url,
          categoryId: event.categoryId
        };
      });
  }, [events, categories, activeCategory]);

  // Get events for a specific date
  const getEventsForDate = useCallback((date: Date) => {
    const dateKey = date.toDateString();
    return eventsByDate[dateKey] || [];
  }, [eventsByDate]);

  // Toggle active category filter
  const toggleCategoryFilter = useCallback((categoryId: string) => {
    storeToggleCategoryFilter(categoryId);
  }, [storeToggleCategoryFilter]);

  // Clear active category filter
  const clearCategoryFilter = useCallback(() => {
    storeClearCategoryFilter();
  }, [storeClearCategoryFilter]);

  // Load data on mount
  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  return {
    events,
    categories,
    formattedEvents,
    eventsByDate,
    eventsByCategory,
    activeCategory,
    loading,
    error,
    currentEvent,
    loadingEvent,
    fetchCalendarData,
    fetchEvent,
    createEvent,
    updateEvent,
    deleteEvent,
    createCategory,
    updateCategory,
    deleteCategory,
    getEventsForDate,
    toggleCategoryFilter,
    clearCategoryFilter
  };
};
